<template>
    <tr class="sub-child-row">
        <v-lazy v-for="(header, index) in headers"
                :key="index"
                tag="td"
                :class="'text-' + header.align"
                style="height:20px; border: 0">
            <div :class="header.class"
                 class="main--text body-2">
                <span v-if="header.value === 'avatar'">
                    <v-row no-gutters
                           class="fill-height">
                        <v-col style="min-width: 307px; width: 307px; max-width: 307px;">
                            <v-row align="center"
                                   style="flex-wrap: nowrap">
                                <div style="width: 91px; min-width: 91px"></div>
                                <span class="caption text-none text-truncate font-weight-medium grey--text text--darken-4 mr-2"
                                      style="cursor: pointer;"
                                      @click="onItemTitleClick(item)">
                                    {{ getSubChildTitle(item) }}
                                </span>
                            </v-row>
                        </v-col>
                    </v-row>
                </span>
                <span v-else-if="header.value === 'progress'">
                    <v-row no-gutters
                           align="center"
                           class="fill-height">
                        <div class="progress-wrapper"
                             style="width: 100%">
                            <v-progress-linear class="report-progress-colors"
                                               :buffer-value="primaryValue"
                                               :value="secondaryValue"
                                               color="primary"
                                               height="7"
                                               reactive/>
                        </div>
                    </v-row>
                </span>
                <span v-else-if="header.value === 'extra'">
                    <v-row no-gutters
                           align="center"
                           :justify="header.justify"
                           class="fill-height px-3">
                        <span v-if="item.total"
                              class="grey--text text--darken-4 caption font-weight-medium">
                            {{ formatTimeDuration(item.extra) }}
                        </span>
                        <div v-else
                             class="progress-wrapper"
                             style="height: 7px; width: 27px"></div>
                    </v-row>
                </span>
                <span v-else-if="header.value === 'total'">
                    <v-row no-gutters
                           align="center"
                           :justify="header.justify"
                           class="fill-height px-3">
                        <span v-if="item.total"
                              class="grey--text text--darken-4 caption font-weight-medium">
                            {{ formatTimeDuration(item.total) }}
                        </span>
                        <div v-else
                             class="progress-wrapper"
                             style="height: 7px; width: 27px"></div>
                    </v-row>
                </span>
                <v-row v-else
                       no-gutters
                       :justify="header.justify"
                       class="fill-height">
                    <span v-if="item[header.value]"
                          class="caption">
                        {{ formatByHeader(item[header.value], header) }}</span>
                    <div v-else
                         class="progress-wrapper"
                         style="height: 7px; width: 27px"></div>
                </v-row>
            </div>
        </v-lazy>
    </tr>
</template>

<script>
import {$t} from "@/i18n";
import ReportModule from "../../store/modules/report";
import BookmarkModule from "../../store/modules/bookmark";
import ReportDefaultColumns from "./ReportDefaultColumns";
import {
    formatDateShort,
    formatWeekdayShort,
    formatDateCustom,
    parseSysDate,
    parseSysDateTime,
    formatTime,
    calcDateEquals,
    formatTimeDuration,
    formatByHeader,
    CustomFormats,
} from "@/plugins/formatting";
import AppModule from "@/store/modules/app";

export default {
    name: "ReportSummaryChildRow",

    props: {
        headers: {
            type: Array,
            default: () => []
        },
        item: {
            type: Object,
            required: true
        },
        primaryValue: {
            type: Number,
            default: 0
            // percentageFromMax(item)
        },
        secondaryValue: {
            type: Number,
            default: 0
            // extraPercentage(item)
        },
        grouping: {
            type: String,
            required: true
        },
        parentId: {
            type: [String, Number],
            required: true
        },
        urlAdapter: {
            type: Object,
            default: () => ({}),
        },
    },

    setup(props) {
        const {default_task_columns} = ReportDefaultColumns();
        const appModule = AppModule();
        const {navigate_report_default_route} = BookmarkModule();
        const {topics_map, calcExtraColumnValue, projects_map} = ReportModule();

        const getSubChildTitle = subchild => {
            if (!subchild || !subchild.sub_group_id) return "false";
            const current_grouping = String(props.grouping).split(";");
            const secondary_grouping = current_grouping[1];
            if (secondary_grouping === "topic") {
                if (topics_map.value[String(subchild.sub_group_id).split(";")[0]]) {
                    return topics_map.value[String(subchild.sub_group_id).split(";")[0]].name;
                } else {
                    return $t("Unknown");
                }
            } else if (secondary_grouping === "project") {
                if (projects_map.value[String(subchild.sub_group_id)]) {
                    return projects_map.value[String(subchild.sub_group_id)].name;
                } else {
                    return $t("Unknown");
                }
            } else if (secondary_grouping === "title") {
                return subchild.sub_group_id;
            } else if (secondary_grouping === "assignee") {
                return subchild.name;
            } else if (secondary_grouping === "client") {
                if (subchild.client) {
                    return subchild.client.name;
                }
                let client_id = String(subchild.sub_group_id).split("_")[1];
                return "#{0}".format(client_id);
            } else if (secondary_grouping === "date") {
                if (String(subchild.sub_group_id).startsWith("W")) {
                    let year_month_week = subchild.sub_group_id.substr(1);
                    let [year, week] = year_month_week.split("_");
                    let is_this_year = calcDateEquals(new Date(), new Date(year, 0, 1), "year");
                    let year_str = is_this_year ? "" : ", " + year;
                    return $t("Week") + " " + week + year_str;
                } else if (subchild.sub_group_id.length === 13) {
                    return formatTime(parseSysDateTime(subchild.sub_group_id + ":00"));
                } else if (subchild.sub_group_id.length === 10) {
                    return (
                        formatDateShort(subchild.sub_group_id) +
                        " " +
                        formatWeekdayShort(parseSysDate(subchild.sub_group_id))
                    );
                } else if (subchild.sub_group_id.length === 7) {
                    return formatDateCustom(parseSysDate(subchild.sub_group_id + "-01"), CustomFormats.short_month_name_with_full_year);
                }
            }
        };

        const onItemTitleClick = item => {
            let queryParams = {}
            let add_dates_list = true;
            const current_grouping = String(props.grouping).split(";");
            const primary_grouping = current_grouping[0];
            const secondary_grouping = current_grouping[1];

            if (primary_grouping === "client") {
                queryParams = Object.assign(queryParams, {
                    client_id_list: Number(props.parentId.split("_")[1])
                });
            } else if (primary_grouping === "assignee") {
                queryParams = Object.assign(queryParams, {
                    person_id_list: Number(props.parentId)
                });
            } else if (primary_grouping === "date") {
                add_dates_list = false;
                queryParams = Object.assign(queryParams, {
                    dates_list: [props.parentId, props.parentId]
                });
            }

            if (secondary_grouping === "topic") {
                queryParams = Object.assign(queryParams, {
                    topic_id_list: item.sub_group_id
                });
            } else if (secondary_grouping === "assignee") {
                queryParams = Object.assign(queryParams, {
                    person_id_list: Number(item.sub_group_id)
                });
            } else if (secondary_grouping === "client") {
                queryParams = Object.assign(queryParams, {
                    client_id_list: Number(item.sub_group_id.split("_")[1])
                });
            } else if (secondary_grouping === "title") {
                queryParams = Object.assign(queryParams, {
                    keyword: item.sub_group_id
                });
            } else if (secondary_grouping === "date") {
                add_dates_list = false;
                queryParams = Object.assign(queryParams, {
                    dates_list: [item.sub_group_id, item.sub_group_id]
                });
            }

            if (add_dates_list) {
                queryParams = Object.assign(queryParams, {
                    dates_list: props.urlAdapter.getUrlParameter("dates_list")
                });
            }

            queryParams = Object.assign(queryParams, {
                view: "detailed",
                columns: default_task_columns,
                groupBy: props.grouping,
            });
            navigate_report_default_route(queryParams);
        };

        return {
            calcExtraColumnValue,
            getSubChildTitle,
            onItemTitleClick,
            formatTimeDuration,
            formatByHeader,
        };
    }
};
</script>
